import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { discoverActions } from '../+state/discover.actions';
import { discoverFeature } from '../+state/discover.reducer';
import {
  selectDiscoverIndices,
  selectDiscoverKnowHow,
  selectDiscoverListAndLinks,
  selectDiscoverListDetailsInstruments,
  selectDiscoverMegatrends,
  selectDiscoverTodayTeaser,
  selectDiscoverTopPerformerGroups,
} from '../+state/discover.selectors';
import { MultiOrderStrategiesOverviewType } from '../entities/multi-order-strategy.entity';

@Injectable({ providedIn: 'root' })
export class DiscoverFacade {
  readonly #store = inject(Store);

  public readonly overviewLoading$ = this.#store.select(discoverFeature.selectOverviewLoading);
  public readonly overviewInitiallyLoaded$ = this.#store.select(discoverFeature.selectOverviewInitiallyLoaded);
  public readonly indices$ = this.#store.select(selectDiscoverIndices);
  public readonly todayTeaser$ = this.#store.select(selectDiscoverTodayTeaser);
  public readonly listsAndLinks$ = this.#store.select(selectDiscoverListAndLinks);
  public readonly knowHow$ = this.#store.select(selectDiscoverKnowHow);
  public readonly megatrends$ = this.#store.select(selectDiscoverMegatrends);
  public readonly topPerformerGroups$ = this.#store.select(selectDiscoverTopPerformerGroups);

  public readonly discoverDetails$ = this.#store.select(discoverFeature.selectDetails);
  public readonly discoverDetailsLoading$ = this.#store.select(discoverFeature.selectDetailsLoading);
  public readonly discoverDetailsQuotesReady$ = this.#store.select(discoverFeature.selectDetailsQuotesReady);

  public readonly discoverListDetails$ = this.#store.select(discoverFeature.selectListDetails);
  public readonly discoverListDetailsLoading$ = this.#store.select(discoverFeature.selectListDetailsLoading);
  public readonly discoverListDetailsInstruments$ = this.#store.select(selectDiscoverListDetailsInstruments);
  public readonly discoverListDetailsQuotesReady$ = this.#store.select(discoverFeature.selectListDetailsQuotesReady);

  public readonly multiOrderStrategies$ = this.#store.select(discoverFeature.selectMultiOrderStrategies);
  public readonly multiOrderStrategy$ = this.#store.select(discoverFeature.selectMultiOrderStrategy);
  public readonly multiOrderStrategyAssetsLoaded$ = this.#store.select(discoverFeature.selectMultiOrderStrategyAssetsLoaded);
  public readonly multiOrderStrategiesLoading$ = this.#store.select(discoverFeature.selectMultiOrderStrategiesLoading);

  public loadDiscoverOverview(force = false): void {
    this.#store.dispatch(discoverActions.loadDiscoverOverview({ force }));
  }

  public subscribeToIndices(): void {
    this.#store.dispatch(discoverActions.subscribeToIndices());
  }

  public unsubscribeFromIndices(): void {
    this.#store.dispatch(discoverActions.unsubscribeFromIndices());
  }

  public clearDiscoverDetails(): void {
    this.#store.dispatch(discoverActions.clearDiscoverDetails());
  }

  public loadDiscoverTodayDetails(id: string): void {
    this.#store.dispatch(discoverActions.loadDiscoverTodayDetails({ id }));
  }

  public loadDiscoverMegatrendsDetails(id: string) {
    this.#store.dispatch(discoverActions.loadDiscoverMegatrendsDetails({ id }));
  }

  public loadDiscoverKnowHowDetails(id: string): void {
    this.#store.dispatch(discoverActions.loadDiscoverKnowHowDetails({ id }));
  }

  public subscribeToDetailsInstruments(): void {
    this.#store.dispatch(discoverActions.subscribeToDetailsInstruments());
  }

  public unsubscribeFromDetailsInstruments(): void {
    this.#store.dispatch(discoverActions.unsubscribeFromDetailsInstruments());
  }

  public loadDiscoverListAndLinksDetails(id: string): void {
    this.#store.dispatch(discoverActions.loadDiscoverListDetails({ id }));
  }

  public subscribeToListInstruments(): void {
    this.#store.dispatch(discoverActions.subscribeToListInstruments());
  }

  public unsubscribeFromListInstruments(): void {
    this.#store.dispatch(discoverActions.unsubscribeFromListInstruments());
  }

  public subscribeToTopPerformerInstruments(groupIdentifier: string): void {
    this.#store.dispatch(discoverActions.subscribeToTopPerformerInstruments({ groupIdentifier }));
  }

  public unsubscribeFromTopPerformerInstruments(groupIdentifier: string): void {
    this.#store.dispatch(discoverActions.unsubscribeFromTopPerformerInstruments({ groupIdentifier }));
  }

  public loadMultiOrderStrategies(strategyType: MultiOrderStrategiesOverviewType = 'investmentTeaser'): void {
    this.#store.dispatch(discoverActions.loadMultiOrderStrategies({ strategyType }));
  }

  public clearMultiOrderStrategies(): void {
    this.#store.dispatch(discoverActions.clearMultiOrderStrategies());
  }

  public loadMultiOrderStrategy(id: string): void {
    this.#store.dispatch(discoverActions.loadMultiOrderStrategy({ id }));
  }

  public clearMultiOrderStrategy(): void {
    this.#store.dispatch(discoverActions.clearMultiOrderStrategy());
  }
}
